import { useState } from 'react';

import {
  useContract,
  useCreateSeason,
  useCreateShow,
  useShows,
  useUpdateContract,
} from '../../api/hooks';
import { Button, FormField, Select, TextInput, useToast } from '../../components/DesignSystem';
import { errorMessage } from '../../lib/errors';
import { seasonSlug, showSlug } from '../../lib/slug';
import { BackendShareTable } from './BackendShareTable';
import { DistributionDeductionCard } from './DistributionDeductionCard';
import { MANUAL_REVIEW_MESSAGES } from './manualReview';
import type { ManualReviewReason } from './manualReview';
import { ReportedCurrencyCard } from './ReportedCurrencyCard';
import { ResidualDefaultsSection } from './ResidualDefaultsSection';

const NEW_SHOW_OPTION = '__new-show';
const NEW_SEASON_OPTION = '__new-season';

interface StepReviewSharesProps {
  contractId: string;
  manualReviewReason: ManualReviewReason | null;
  onBack: () => void;
  onNext: () => void;
}

/** Step 2 — title metadata, reported currency, backend shares, and residual defaults. */
export function StepReviewShares({
  contractId,
  manualReviewReason,
  onBack,
  onNext,
}: StepReviewSharesProps) {
  const { showToast } = useToast();
  const contract = useContract(contractId);
  const shows = useShows({ limit: 100 });

  const createShow = useCreateShow();
  const createSeason = useCreateSeason();
  const updateContract = useUpdateContract();

  const [showChoice, setShowChoice] = useState('');
  const [newShowName, setNewShowName] = useState('');
  const [seasonChoice, setSeasonChoice] = useState('');
  const [newSeasonCode, setNewSeasonCode] = useState('');
  const [isSavingShow, setIsSavingShow] = useState(false);

  const showList = shows.data?.data ?? [];
  const effectiveShowId = showChoice === '' ? (contract.data?.showId ?? '') : showChoice;
  const selectedShow = showList.find((show) => show.id === effectiveShowId);
  const effectiveSeasonId = seasonChoice === '' ? (contract.data?.seasonId ?? '') : seasonChoice;
  const selectedSeason = selectedShow?.seasons.find((season) => season.id === effectiveSeasonId);

  async function handleSaveShow() {
    if (isSavingShow) {
      return;
    }

    setIsSavingShow(true);

    try {
      const showId =
        effectiveShowId === NEW_SHOW_OPTION
          ? (await createShow.mutateAsync({ name: newShowName.trim() })).id
          : effectiveShowId;

      if (showId === '') {
        showToast('Select or create a show first', 'error');
        return;
      }

      // A brand-new show can't reuse the previous show's season id.
      const wantsNewSeason =
        effectiveSeasonId === NEW_SEASON_OPTION ||
        (effectiveShowId === NEW_SHOW_OPTION && newSeasonCode.trim() !== '');

      const seasonId = wantsNewSeason
        ? (
            await createSeason.mutateAsync({
              showId,
              input: {
                number: (selectedShow?.seasons.length ?? 0) + 1,
                seasonCode: newSeasonCode.trim(),
              },
            })
          ).id
        : effectiveShowId === NEW_SHOW_OPTION
          ? ''
          : effectiveSeasonId;

      await updateContract.mutateAsync({
        contractId,
        input: { showId, ...(seasonId !== '' ? { seasonId } : {}) },
      });
      showToast('Show and season saved');
      setShowChoice('');
      setSeasonChoice('');
    } catch (error) {
      showToast(errorMessage(error, 'Could not save show and season'), 'error');
    } finally {
      setIsSavingShow(false);
    }
  }

  return (
    <>
      {manualReviewReason && (
        <p className="rfp-notice" role="status">
          {MANUAL_REVIEW_MESSAGES[manualReviewReason]}
        </p>
      )}

      <section className="rfp-card">
        <div className="rfp-card__head">
          <div>
            <h2 className="rfp-card__title">Title Metadata</h2>
            <p className="rfp-card__caption">
              {contract.isLoading
                ? 'Loading detected terms…'
                : contract.data?.showId
                  ? 'Detected from the contract — adjust if needed.'
                  : 'No show detected — select an existing show or create one.'}
            </p>
          </div>
        </div>

        <div className="rfp-form-grid">
          <FormField htmlFor="review-show" label="Show Name">
            <Select
              id="review-show"
              onChange={(event) => {
                setShowChoice(event.target.value);
                setSeasonChoice('');
              }}
              value={effectiveShowId}
            >
              <option value="">Select a show…</option>
              {showList.map((show) => (
                <option key={show.id} value={show.id}>
                  {show.name}
                </option>
              ))}
              <option value={NEW_SHOW_OPTION}>+ Create new show…</option>
            </Select>
          </FormField>

          {effectiveShowId === NEW_SHOW_OPTION ? (
            <FormField htmlFor="review-show-name" label="New Show Name">
              <TextInput
                id="review-show-name"
                onChange={(event) => setNewShowName(event.target.value)}
                placeholder="e.g. Harbour Lights"
                value={newShowName}
              />
            </FormField>
          ) : (
            <FormField htmlFor="review-season" label="Season Number / Name">
              <Select
                id="review-season"
                onChange={(event) => setSeasonChoice(event.target.value)}
                value={effectiveSeasonId}
              >
                <option value="">No season</option>
                {(selectedShow?.seasons ?? []).map((season) => (
                  <option key={season.id} value={season.id}>
                    {season.seasonCode}
                    {season.name ? ` — ${season.name}` : ''}
                  </option>
                ))}
                <option value={NEW_SEASON_OPTION}>+ Add season…</option>
              </Select>
            </FormField>
          )}

          {(effectiveSeasonId === NEW_SEASON_OPTION || effectiveShowId === NEW_SHOW_OPTION) && (
            <FormField htmlFor="review-season-code" label="New Season Code">
              <TextInput
                id="review-season-code"
                onChange={(event) => setNewSeasonCode(event.target.value)}
                placeholder="e.g. S1"
                value={newSeasonCode}
              />
            </FormField>
          )}

          <FormField htmlFor="review-parent-id" label="Parent ID (auto-generated)">
            <TextInput
              disabled
              id="review-parent-id"
              value={selectedShow ? showSlug(selectedShow.name) : '—'}
            />
          </FormField>
          <FormField htmlFor="review-season-id" label="Season ID (auto-generated)">
            <TextInput
              disabled
              id="review-season-id"
              value={
                selectedShow && selectedSeason
                  ? seasonSlug(selectedShow.name, selectedSeason.seasonCode, selectedSeason.name)
                  : '—'
              }
            />
          </FormField>
        </div>

        <div className="rfp-wizard-actions">
          <Button
            disabled={isSavingShow}
            onClick={handleSaveShow}
            type="button"
            variant="secondary"
          >
            {isSavingShow ? 'Saving…' : 'Save show & season'}
          </Button>
        </div>
      </section>

      <ReportedCurrencyCard contractId={contractId} />

      <DistributionDeductionCard contractId={contractId} />

      <BackendShareTable contractId={contractId} />

      <ResidualDefaultsSection contractId={contractId} />

      <div className="rfp-wizard-actions">
        <Button onClick={onBack} type="button" variant="secondary">
          Back
        </Button>
        <Button onClick={onNext} type="button">
          Continue <span aria-hidden="true">→</span>
        </Button>
      </div>
    </>
  );
}
